import Lottie from 'lottie-react';
import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import percentProgressAnimation from '@/assets/animations/PercentProgress.json';
import styles from './ProcessingScreen.module.css';
import type { ProcessingScreenProps } from './ProcessingScreen.types';

const ProcessingScreen = ({
  imageUrls,
  onComplete,
}: ProcessingScreenProps): JSX.Element => {
  const [currentVideoIndex, setCurrentVideoIndex] = useState<number>(0);
  const [, setIsPlaying] = useState<boolean>(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isFading, setIsFading] = useState(false);

  // Array of video paths - playing sequentially
  const videos = useMemo(
    () => [
      'https://d19dlu1w9mnmln.cloudfront.net/searchAnimation.mp4',
      'https://d19dlu1w9mnmln.cloudfront.net/curation.mp4',
    ],
    []
  );

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleVideoEnd = (): void => {
      if (currentVideoIndex < videos.length - 1) {
        setIsFading(true); // Start fade out
        setTimeout(() => {
          setCurrentVideoIndex((prev) => prev + 1);
        }, 400); // Match fade duration
      } else {
        onComplete(imageUrls);
      }
    };

    const handleVideoLoad = (): void => {
      setIsFading(false); // Fade in when video is ready
      video
        .play()
        .then(() => {
          setIsPlaying(true);
        })
        .catch((error) => {
          console.error('Error playing video:', error);
          handleVideoEnd();
        });
    };

    video.addEventListener('ended', handleVideoEnd);
    video.addEventListener('loadeddata', handleVideoLoad);

    // Set the current video source
    video.src = videos[currentVideoIndex];
    video.load();

    return () => {
      video.removeEventListener('ended', handleVideoEnd);
      video.removeEventListener('loadeddata', handleVideoLoad);
    };
  }, [currentVideoIndex, videos, onComplete, imageUrls]);

  return (
    <div className={styles.overlay}>
      <div className={styles.responsiveContainer}>
        <div className={styles.lottieProgressOverlay}>
          <Lottie
            autoplay
            animationData={percentProgressAnimation}
            className={styles.lottieProgress}
            loop={false}
          />
        </div>
        <div className={styles.contentContainer}>
          <div className={styles.content}>
            {/* Show curationText only when the second video is playing */}
            {currentVideoIndex === 1 && (
              <div className={styles.curationTextOverlay}>
                <div className={styles.curationSub}>And browse</div>
                <div className={styles.curationTitle}>Handpicked curations</div>
              </div>
            )}
          </div>
          <div className={styles.videoContainer}>
            <video
              muted
              playsInline
              autoPlay
              ref={videoRef}
              className={`${styles.video} ${currentVideoIndex === 0 ? styles.videoNarrow : ''} ${isFading ? styles.videoFade : ''}`}
            >
              <source src={videos[currentVideoIndex]} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProcessingScreen;
